import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Check, ChevronsUpDown, Plus, LayoutGrid } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";

export default function WorkspaceSwitcher() {
  const { workspace, workspaces, switchWorkspace, createWorkspace } = useAuth();
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (menuRef.current?.contains(e.target)) return;
      if (btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const doSwitch = async (id) => {
    if (id === workspace?.id) { setOpen(false); return; }
    try {
      await switchWorkspace(id);
      toast.success("Switched workspace");
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to switch");
    }
  };

  const doCreate = async (e) => {
    e.preventDefault();
    if (!name.trim() || creating) return;
    setCreating(true);
    try {
      await createWorkspace(name.trim());
      toast.success("Workspace created");
      setShowCreate(false);
      setName("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create workspace");
    } finally { setCreating(false); }
  };

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 rounded border border-border/60 bg-secondary/30 hover:bg-secondary/60 transition-colors text-left"
        data-testid="workspace-switcher-btn"
      >
        <LayoutGrid className="w-4 h-4 shrink-0 text-muted-foreground" />
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Workspace</p>
          <p className="text-sm font-medium truncate" data-testid="sidebar-workspace-name">
            {workspace?.name || "—"}
          </p>
        </div>
        <ChevronsUpDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
      </button>

      {open && (
        <div
          ref={menuRef}
          className="absolute z-30 left-0 right-0 mt-2 rounded-lg border border-border/60 bg-popover shadow-lg overflow-hidden"
          data-testid="workspace-switcher-menu"
        >
          <div className="max-h-72 overflow-auto py-1">
            {workspaces.length === 0 && (
              <div className="px-3 py-3 text-xs text-muted-foreground">Loading…</div>
            )}
            {workspaces.map((w) => (
              <button
                key={w.id}
                onClick={() => doSwitch(w.id)}
                className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-secondary/60"
                data-testid={`workspace-option-${w.id}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="truncate">{w.name}</p>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{w.role}</p>
                </div>
                {w.id === workspace?.id && <Check className="w-3.5 h-3.5 text-foreground shrink-0" />}
              </button>
            ))}
          </div>
          <div className="border-t border-border/60">
            <button
              onClick={() => { setOpen(false); setShowCreate(true); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-secondary/60 text-muted-foreground"
              data-testid="workspace-new-btn"
            >
              <Plus className="w-3.5 h-3.5" /> New workspace
            </button>
          </div>
        </div>
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">New workspace</DialogTitle>
            <DialogDescription>You'll be the Admin of the new workspace and switched into it right away.</DialogDescription>
          </DialogHeader>
          <form onSubmit={doCreate} className="mt-4 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="ws-name">Name</Label>
              <Input
                id="ws-name"
                autoFocus
                placeholder="e.g. Acme Support Team"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="workspace-new-name-input"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="ghost" onClick={() => setShowCreate(false)} className="rounded-full">Cancel</Button>
              <Button type="submit" disabled={creating || !name.trim()} className="rounded-full" data-testid="workspace-new-create-btn">
                {creating ? "Creating…" : "Create"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
